import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useMemo, useState } from "react";

import { SortHeading, compareNullableSortValues, type TableSort } from "./dashboardTableSorting";

import type {
  RetellCallsCareSpaceAgeCohort,
  RetellCallsClinicBreakdownRow,
  RetellCallsTransferReportRow,
} from "./retellCallsTypes";
import {
  DEEP,
  GRID,
  HAIR,
  INK,
  MUTED,
  ORANGE,
  POS,
  SKY,
  TEAL,
  fmtDateLabel,
  fmtInt,
  fmtPct,
} from "./retellCallsTheme";
import { CsvButton, LegendSwatch, Section, TableHeader, tableCellStyle } from "./retellCallsUi";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
);

function getTransferCsvColumns(connectedThresholdSeconds: number, pickupThresholdSeconds: number) {
  return [
    { key: "day", label: "Date" },
    { key: "totalRetellCalls", label: "Total Retell calls" },
    { key: "completedTransfers", label: "Completed transfers" },
    {
      key: "pipelinePickedUp",
      label: `Pipeline picked up >=${pickupThresholdSeconds}s`,
    },
    { key: "pipelinePickupRatePct", label: "Pipeline pickup rate %" },
    { key: "transferRequests", label: "Transfer requests" },
    { key: "matchedDialpadCalls", label: "Matched Dialpad calls" },
    { key: "markedTransferred", label: "Marked transferred" },
    { key: "dialpadPickedUp", label: "Dialpad picked up" },
    { key: "connected", label: `Connected >=${connectedThresholdSeconds}s` },
    { key: "phoneTimeMatches", label: "Phone/time matches" },
    { key: "transferredTimeMatches", label: "Transferred/time matches" },
    { key: "requestRatePct", label: "Transfer request rate %" },
    { key: "markedTransferRatePct", label: "Marked / request %" },
    { key: "pickupRatePct", label: "Picked up / marked %" },
    { key: "connectedRatePct", label: "Connected / marked %" },
    { key: "connectedRequestRatePct", label: "Connected / request %" },
  ];
}

const transferPanelHeadingStyle = {
  fontWeight: 700,
  fontSize: "0.78rem",
  color: INK,
  margin: "0 0 0.35rem",
};

export function TransferReportSection({
  rows,
  definitions,
}: {
  definitions?: {
    connectedThresholdSeconds?: number;
    lookbackDays?: number;
    pickupThresholdSeconds?: number;
  };
  rows: RetellCallsTransferReportRow[];
}) {
  const labels = rows.map((r) => fmtDateLabel(r.day));
  const totalRequests = rows.reduce((sum, r) => sum + r.transferRequests, 0);
  const totalMarked = rows.reduce((sum, r) => sum + r.markedTransferred, 0);
  const totalConnected = rows.reduce((sum, r) => sum + r.connected, 0);
  const totalCompleted = rows.reduce((sum, r) => sum + (r.completedTransfers || 0), 0);
  const totalPipelinePickedUp = rows.reduce((sum, r) => sum + (r.pipelinePickedUp || 0), 0);
  const pipelinePickupRate = totalCompleted ? (100 * totalPipelinePickedUp) / totalCompleted : 0;
  const lookbackDays = definitions?.lookbackDays ?? 14;
  const connectedThreshold =
    definitions?.connectedThresholdSeconds === undefined
      ? 60
      : definitions.connectedThresholdSeconds;
  const pickupThreshold =
    definitions?.pickupThresholdSeconds === undefined ? 15 : definitions.pickupThresholdSeconds;

  return (
    <Section
      title={`${lookbackDays}-day transfer report`}
      legend={
        <CsvButton
          filename={`retell-transfer-report-${lookbackDays}d.csv`}
          rows={rows as unknown as Array<Record<string, unknown>>}
          columns={getTransferCsvColumns(connectedThreshold, pickupThreshold)}
        />
      }
    >
      {/* Pipeline: did the transfer fire and did a human answer? */}
      <div style={transferPanelHeadingStyle}>Pipeline — completed transfers</div>
      <div style={{ color: MUTED, fontSize: "0.72rem", lineHeight: 1.35 }}>
        Completed transfers are Retell calls with disconnection_reason <b>call_transfer</b> (the
        transfer actually fired). Picked up means the matched inbound Dialpad call had at least{" "}
        {pickupThreshold}s of connected talk time. Pickup rate = picked up / completed transfers.
      </div>

      <div style={{ position: "relative", height: 175, marginTop: "0.7rem" }}>
        <Bar
          data={{
            labels,
            datasets: [
              {
                label: "Completed transfers",
                data: rows.map((r) => r.completedTransfers || 0),
                backgroundColor: TEAL,
              },
              {
                label: `Picked up (>=${pickupThreshold}s)`,
                data: rows.map((r) => r.pipelinePickedUp || 0),
                backgroundColor: POS,
              },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: "bottom",
                labels: { color: MUTED, boxWidth: 10, font: { size: 10 } },
              },
              tooltip: {
                callbacks: {
                  afterBody: (items) => {
                    const row =
                      typeof items[0]?.dataIndex === "number"
                        ? rows[items[0].dataIndex]
                        : undefined;
                    return row ? `Pickup rate: ${fmtPct(row.pipelinePickupRatePct)}` : "";
                  },
                },
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                grid: { color: GRID },
                ticks: { color: MUTED, font: { size: 10 }, precision: 0 },
              },
              x: {
                grid: { display: false },
                ticks: { color: MUTED, font: { size: 10 } },
              },
            },
          }}
        />
      </div>

      <div
        style={{
          display: "flex",
          gap: "1rem",
          color: MUTED,
          fontSize: "0.72rem",
          fontWeight: 700,
          margin: "0.45rem 0 0.55rem",
          flexWrap: "wrap",
        }}
      >
        <span>{fmtInt(totalCompleted)} completed transfers</span>
        <span>{fmtInt(totalPipelinePickedUp)} picked up</span>
        <span>{fmtPct(pipelinePickupRate)} pickup rate</span>
      </div>

      <div style={{ overflowX: "auto", minWidth: 0 }}>
        <table style={{ width: "100%", minWidth: 420, borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <TableHeader label="Date" definition="The day these calls happened." align="left" />
              <TableHeader
                label="Completed"
                definition="Calls that Retell sent on to the call center."
              />
              <TableHeader
                label="Picked up"
                definition={`Calls the call center answered for at least ${pickupThreshold} seconds.`}
              />
              <TableHeader
                label="Pickup rate"
                definition={`The share of completed calls answered for at least ${pickupThreshold} seconds.`}
              />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.day}>
                <td style={{ ...tableCellStyle, textAlign: "left" }}>{fmtDateLabel(row.day)}</td>
                <td style={tableCellStyle}>{fmtInt(row.completedTransfers)}</td>
                <td style={tableCellStyle}>{fmtInt(row.pipelinePickedUp)}</td>
                <td style={tableCellStyle}>{fmtPct(row.pipelinePickupRatePct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <hr
        style={{
          border: 0,
          borderTop: `1px solid ${HAIR}`,
          margin: "1rem 0 0.9rem",
        }}
      />

      {/* Journey: of everyone who wanted a transfer, who connected/converted? */}
      <div style={transferPanelHeadingStyle}>Journey — transfer requests to conversion</div>
      <div style={{ color: MUTED, fontSize: "0.72rem", lineHeight: 1.35 }}>
        Transfer requests use Retell outcome category. Marked transferred means a fuzzy-matched
        inbound Dialpad call has <b>is_transferred</b>. Connected means marked transferred with
        Dialpad talk time at least {connectedThreshold}s. Matching is inferred by phone/time or
        transferred/time; there is no strict Retell-Dialpad join key.
      </div>

      <div style={{ position: "relative", height: 205, marginTop: "0.7rem" }}>
        <Bar
          data={{
            labels,
            datasets: [
              {
                label: "Transfer requests",
                data: rows.map((r) => r.transferRequests),
                backgroundColor: TEAL,
              },
              {
                label: "Marked transferred",
                data: rows.map((r) => r.markedTransferred),
                backgroundColor: DEEP,
              },
              {
                label: "Connected",
                data: rows.map((r) => r.connected),
                backgroundColor: POS,
              },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: "bottom",
                labels: { color: MUTED, boxWidth: 10, font: { size: 10 } },
              },
              tooltip: {
                callbacks: {
                  afterBody: (items) => {
                    const row =
                      typeof items[0]?.dataIndex === "number"
                        ? rows[items[0].dataIndex]
                        : undefined;
                    return row ? `Connected / marked: ${fmtPct(row.connectedRatePct)}` : "";
                  },
                },
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                grid: { color: GRID },
                ticks: { color: MUTED, font: { size: 10 }, precision: 0 },
              },
              x: {
                grid: { display: false },
                ticks: { color: MUTED, font: { size: 10 } },
              },
            },
          }}
        />
      </div>

      <div
        style={{
          display: "flex",
          gap: "1rem",
          color: MUTED,
          fontSize: "0.72rem",
          fontWeight: 700,
          margin: "0.45rem 0 0.55rem",
          flexWrap: "wrap",
        }}
      >
        <span>{fmtInt(totalRequests)} requests</span>
        <span>{fmtInt(totalMarked)} marked transferred</span>
        <span>{fmtInt(totalConnected)} connected</span>
      </div>

      <div style={{ overflowX: "auto", minWidth: 0 }}>
        <table style={{ width: "100%", minWidth: 1080, borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <TableHeader label="Date" definition="The day these calls happened." align="left" />
              <TableHeader
                label="Retell calls"
                definition="All Retell calls that ended that day."
              />
              <TableHeader
                label="Requests"
                definition="Calls where the person asked to talk to the call center."
              />
              <TableHeader
                label="Matched"
                definition="Requests we linked to a call at the call center."
              />
              <TableHeader
                label="Marked"
                definition="Linked calls our phone system says were sent over."
              />
              <TableHeader
                label="Picked up"
                definition="Sent calls that someone at the call center answered."
              />
              <TableHeader
                label="Connected"
                definition={`Sent calls with at least ${connectedThreshold} seconds of talk time.`}
              />
              <TableHeader
                label="Req. rate"
                definition="The share of Retell calls where the person asked for the call center."
              />
              <TableHeader
                label="Marked / req."
                definition="The share of requests our phone system says were sent over."
              />
              <TableHeader
                label="Picked up / marked"
                definition="The share of sent calls someone at the call center answered."
              />
              <TableHeader
                label="Conn. / marked"
                definition={`The share of sent calls with at least ${connectedThreshold} seconds of talk time.`}
              />
              <TableHeader
                label="Conn. / req."
                definition={`The share of requests with at least ${connectedThreshold} seconds of talk time.`}
              />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.day}>
                <td style={{ ...tableCellStyle, textAlign: "left" }}>{fmtDateLabel(row.day)}</td>
                <td style={tableCellStyle}>{fmtInt(row.totalRetellCalls)}</td>
                <td style={tableCellStyle}>{fmtInt(row.transferRequests)}</td>
                <td style={tableCellStyle}>{fmtInt(row.matchedDialpadCalls)}</td>
                <td style={tableCellStyle}>{fmtInt(row.markedTransferred)}</td>
                <td style={tableCellStyle}>{fmtInt(row.dialpadPickedUp)}</td>
                <td style={tableCellStyle}>{fmtInt(row.connected)}</td>
                <td style={tableCellStyle}>{fmtPct(row.requestRatePct)}</td>
                <td style={tableCellStyle}>{fmtPct(row.markedTransferRatePct)}</td>
                <td style={tableCellStyle}>{fmtPct(row.pickupRatePct)}</td>
                <td style={tableCellStyle}>{fmtPct(row.connectedRatePct)}</td>
                <td style={tableCellStyle}>{fmtPct(row.connectedRequestRatePct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

export function CareSpaceAgeCohortSection({ rows }: { rows: RetellCallsCareSpaceAgeCohort[] }) {
  const cohorts = rows.filter((row) => !row.unmatched);
  const unmatched = rows.find((row) => row.unmatched);
  const labels = cohorts.map((row) => row.label);

  return (
    <Section
      title="CareSpace-age cohort funnel"
      legend={
        <>
          <LegendSwatch color={TEAL} label="placed" />
          <LegendSwatch color={SKY} label="answered" />
          <LegendSwatch color={ORANGE} label="intakes" />
        </>
      }
    >
      <div style={{ color: MUTED, fontSize: "0.72rem", lineHeight: 1.35 }}>
        CareSpace age is days since the patient/referral was added to CareSpace, not patient
        biological age. Counts use outbound ended Retell calls in the selected range; unmatched
        CareSpace calls are excluded from the age cohorts
        {unmatched?.placedCalls
          ? ` (${fmtInt(unmatched.placedCalls)} placed · ${fmtInt(unmatched.answeredCalls)} answered · ${fmtInt(unmatched.intakesScheduled)} intakes).`
          : "."}
      </div>

      <div style={{ position: "relative", height: 215, marginTop: "0.7rem" }}>
        <Bar
          data={{
            labels,
            datasets: [
              {
                label: "Placed calls",
                data: cohorts.map((row) => row.placedCalls),
                backgroundColor: TEAL,
              },
              {
                label: "Answered calls",
                data: cohorts.map((row) => row.answeredCalls),
                backgroundColor: SKY,
              },
              {
                label: "Intakes scheduled",
                data: cohorts.map((row) => row.intakesScheduled),
                backgroundColor: ORANGE,
              },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  afterBody: (items) => {
                    const index = items[0]?.dataIndex;
                    const row = typeof index === "number" ? cohorts[index] : undefined;
                    return row
                      ? [
                          `Answered / placed: ${fmtPct(row.answerRatePct)}`,
                          `Scheduled / answered: ${fmtPct(row.scheduledAnswerRatePct)}`,
                        ]
                      : [];
                  },
                },
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                grid: { color: GRID },
                ticks: { color: MUTED, font: { size: 10 }, precision: 0 },
              },
              x: {
                grid: { display: false },
                ticks: { color: MUTED, font: { size: 10 }, maxRotation: 0 },
              },
            },
          }}
        />
      </div>

      <div style={{ overflowX: "auto", marginTop: "0.45rem", minWidth: 0 }}>
        <table style={{ width: "100%", minWidth: 760, borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <TableHeader
                label="CareSpace age"
                definition="How many days had passed since the CareSpace was made."
                align="left"
              />
              <TableHeader label="Placed" definition="Calls Retell made for CareSpaces this old." />
              <TableHeader label="Answered" definition="Placed calls that a person answered." />
              <TableHeader
                label="Intakes"
                definition="Answered calls where an intake was set up."
              />
              <TableHeader
                label="Answered / placed"
                definition="The share of placed calls that a person answered."
              />
              <TableHeader
                label="Scheduled / answered"
                definition="The share of answered calls where an intake was set up."
              />
              <TableHeader
                label="Scheduled / placed"
                definition="The share of placed calls where an intake was set up."
              />
            </tr>
          </thead>
          <tbody>
            {cohorts.map((row) => (
              <tr key={row.key}>
                <td style={{ ...tableCellStyle, textAlign: "left" }}>{row.label}</td>
                <td style={tableCellStyle}>{fmtInt(row.placedCalls)}</td>
                <td style={tableCellStyle}>{fmtInt(row.answeredCalls)}</td>
                <td style={tableCellStyle}>{fmtInt(row.intakesScheduled)}</td>
                <td style={tableCellStyle}>{fmtPct(row.answerRatePct)}</td>
                <td style={tableCellStyle}>{fmtPct(row.scheduledAnswerRatePct)}</td>
                <td style={tableCellStyle}>{fmtPct(row.scheduledPlacedRatePct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

const clinicCsvColumns = [
  { key: "clinic", label: "Clinic" },
  { key: "retellCalls", label: "Retell calls" },
  { key: "humanPickups", label: "Human pickups" },
  { key: "pickupRatePct", label: "Pickup rate %" },
  { key: "intakesScheduled", label: "Intakes scheduled" },
  { key: "intakePickupRatePct", label: "Intake / pickup %" },
];

const clinicTableColumns = [
  { key: "clinic", label: "Clinic", definition: "The clinic tied to the calls in this row." },
  { key: "retellCalls", label: "Retell calls", definition: "Calls Retell made for this clinic." },
  { key: "humanPickups", label: "Human pickups", definition: "Calls that a person answered." },
  {
    key: "pickupRatePct",
    label: "Pickup rate",
    definition: "The share of Retell calls that a person answered.",
  },
  {
    key: "intakesScheduled",
    label: "Intakes scheduled",
    definition: "Answered calls where an intake was set up.",
  },
  {
    key: "intakePickupRatePct",
    label: "Intake / pickup",
    definition: "The share of answered calls where an intake was set up.",
  },
] as const;

type ClinicSortKey = (typeof clinicTableColumns)[number]["key"];
type ClinicSort = TableSort<ClinicSortKey>;

export function sortClinicBreakdownRows(
  rows: RetellCallsClinicBreakdownRow[],
  sort: ClinicSort | null,
) {
  if (!sort) return rows;
  return [...rows].sort((left, right) =>
    compareNullableSortValues(left[sort.key], right[sort.key], sort.direction),
  );
}

function RateTableCell({ value, color }: { color: string; value: number }) {
  return (
    <td style={{ ...tableCellStyle, minWidth: 150 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(70px, 1fr) 44px",
          alignItems: "center",
          gap: "0.45rem",
        }}
      >
        <div
          style={{
            height: 6,
            background: GRID,
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${Math.max(0, Math.min(100, value))}%`,
              height: "100%",
              background: color,
              borderRadius: 3,
            }}
          />
        </div>
        <span>{fmtPct(value)}</span>
      </div>
    </td>
  );
}

export function ClinicBreakdownSection({ rows }: { rows: RetellCallsClinicBreakdownRow[] }) {
  const [sort, setSort] = useState<ClinicSort | null>(null);
  const sortedRows = useMemo(() => sortClinicBreakdownRows(rows, sort), [rows, sort]);

  function updateSort(key: ClinicSortKey) {
    setSort((current) => ({
      key,
      direction:
        current?.key === key
          ? current.direction === "asc"
            ? "desc"
            : "asc"
          : key === "clinic"
            ? "asc"
            : "desc",
    }));
  }

  return (
    <Section
      title="Clinic call performance"
      legend={
        <CsvButton
          filename="retell-clinic-performance.csv"
          rows={sortedRows}
          columns={clinicCsvColumns}
        />
      }
    >
      <div style={{ color: MUTED, fontSize: "0.72rem", lineHeight: 1.35 }}>
        Outbound ended Retell calls in the selected range, attributed through the same CareSpace
        matching used above. Pickup rate is human pickups / Retell calls; intake schedule rate is
        scheduled intakes / human pickups. Unmatched calls remain visible for reconciliation. Click
        a column heading to sort.
      </div>

      <div style={{ overflowX: "auto", marginTop: "0.7rem", minWidth: 0 }}>
        <table
          aria-label="Clinic call performance"
          style={{ width: "100%", minWidth: 760, borderCollapse: "collapse" }}
        >
          <thead>
            <tr>
              {clinicTableColumns.map(({ key, label, definition }) => (
                <TableHeader
                  key={key}
                  label={label}
                  definition={definition}
                  align={key === "clinic" ? "left" : "right"}
                  sortDirection={
                    sort?.key === key
                      ? sort.direction === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                >
                  <SortHeading
                    column={key}
                    label={label}
                    sort={sort}
                    onSort={updateSort}
                    style={{
                      font: "inherit",
                      color: "inherit",
                      background: "transparent",
                      border: 0,
                      padding: 0,
                      cursor: "pointer",
                    }}
                  />
                </TableHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => (
              <tr key={row.key}>
                <td
                  style={{
                    ...tableCellStyle,
                    textAlign: "left",
                    color: row.unmatched ? MUTED : INK,
                    fontWeight: row.unmatched ? 600 : 700,
                  }}
                >
                  {row.clinic}
                </td>
                <td style={tableCellStyle}>{fmtInt(row.retellCalls)}</td>
                <td style={tableCellStyle}>{fmtInt(row.humanPickups)}</td>
                <RateTableCell value={row.pickupRatePct} color={SKY} />
                <td style={tableCellStyle}>{fmtInt(row.intakesScheduled)}</td>
                <RateTableCell value={row.intakePickupRatePct} color={ORANGE} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}
